import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Wrench, Globe2, ShieldCheck, MapPin, Truck } from "lucide-react";
import { HERO_IMAGES } from "@/data/products";
import { ROOF_HERO_IMAGES } from "@/data/roofracks";
import { ROOF_TENTS, AWNINGS } from "@/data/categories";
import { SEO, buildOrganizationJsonLd } from "@/components/SEO";
import { StaggerChildren, StaggerItem } from "@/components/Motion";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { RecentlyViewedStrip } from "@/components/RecentlyViewedStrip";
import { useCategoryOrder, sortBySlug } from "@/hooks/useCategoryOrder";
import { FEATURE_FLAGS } from "@/config/featureFlags";

const BRANDS = [
  {
    name: "BUSHTECH",
    origin: "South Africa",
    tagline: "Aluminium ute canopies, service bodies and campers",
    desc: "The engineering benchmark for aluminium ute canopies — used by global overland fleets and NZ dealerships. TIG-welded frames. 10-year frame guarantee.",
  },
  {
    name: "EEZI-AWN",
    origin: "South Africa",
    tagline: "Roof tents, awnings, K9 racks and expedition kit",
    desc: "One of the most respected names in overlanding. Roof tents, awnings and K9 platform racks trusted on cross-continent expeditions for 40 years.",
  },
  {
    name: "QUICK PITCH",
    origin: "South Africa",
    tagline: "Awnings, roof tents and other accessories",
    desc: "Purpose-built for real overland use — awnings, roof tents and other accessories. Quicker deployment, tougher frames, better wind resistance than mainstream alternatives.",
  },
];

const CATEGORIES = [
  ...(FEATURE_FLAGS.HARD_LIDS_ENABLED
    ? [{
        n: "00",
        title: "HARD LIDS",
        sub: "Tour Top Cover — lockable lid & roof rack in one, coming soon",
        image: "/products/hard-lids/lock-detail.webp",
        hoverImage: "/products/hard-lids/open-bed-1.webp",
        to: "/hard-lids",
        // This source photo already has a dark background (blurred night/
        // shadow scene), so the shared black/90 overlay used by every other
        // tile makes it read as too blacked-out. Lighter override just here.
        gradientOverlay: "from-black/70 via-black/15 to-transparent",
      }]
    : []),
  {
    n: "01",
    title: "WELLSIDE CANOPIES",
    sub: "TIG-welded aluminium ute canopies",
    image: HERO_IMAGES.wellsideHilux,
    hoverImage: "/products/wellsides/731077_e99532b6f8934f559e49154524d312f6~mv2.jpg",
    to: "/wellside-canopies",
  },
  {
    n: "02",
    title: "X-SERIES TRAY & CANOPY",
    sub: "Expedition-grade service body",
    image: HERO_IMAGES.xseriesDuo,
    hoverImage: "/products/xseries/731077_c695061aca5340b7aa4b31136a2c0a98~mv2.jpg",
    hoverImageZoom: 1.35,
    to: "/x-series-tray",
  },
  {
    n: "03",
    title: "BUSH CABIN UTE CAMPER",
    sub: "Fully insulated live-in ute camper",
    image: "/products/bushcabin/bushcabin-tile-main-closed-beach.webp",
    hoverImage: "/products/bushcabin/bushcabin-tile-hover-open-beach.webp",
    to: "/bush-cabin",
  },
  {
    n: "04",
    title: "K9 ROOF RACKS",
    sub: "Load bars · racks · mounts · accessories",
    image: ROOF_HERO_IMAGES.hero,
    hoverImage: ROOF_HERO_IMAGES.landcruiser,
    to: "/roof-racks",
  },
  {
    n: "05",
    title: "AWNINGS",
    sub: "Freestanding · Wind deflectors · LED lighting",
    image: AWNINGS.categories[0].image,
    hoverImage: "/tiles/home-awnings-hover.jpg",
    to: "/awnings",
  },
  {
    n: "06",
    title: "ROOF TENTS",
    sub: "Hard shell · Soft shell · Side opening",
    image: "/tiles/home-rooftents.jpg",
    hoverImage: ROOF_TENTS.categories[1].image,
    to: "/roof-tents",
  },
  {
    n: "07",
    title: "GROUND TENTS",
    sub: "Commercial-grade canvas ground tents",
    image: "/products/tents/731077_d0cc2bb8e75f487a979b330efd4fe2a5~mv2.jpg",
    hoverImage: "/tiles/home-canvastents-hover.jpg",
    to: "/ground-tents",
  },
  {
    n: "08",
    title: "SUZUKI JIMNY STORE",
    sub: "Serious kit — dedicated to the Jimny",
    image: "/products/bullbars/731077_90f2cc9e9d2c425d8ef4d8fa40532751~mv2.jpg",
    hoverImage: "/products/jimny/731077_6f5675613e92452c8e2a59c3958e1904~mv2.jpg",
    to: "/jimny-store",
  },
  {
    n: "09",
    title: "DRAWERS & KITCHENS",
    sub: "Fridge slides · Storage systems · Tables",
    image: "/products/drawers/731077_5d9e25809aea44d998cf28168b514f84~mv2.jpg",
    hoverImage: "/tiles/home-drawers-hover.jpg",
    to: "/drawers-kitchens-tables",
  },
  {
    n: "10",
    title: "12V FRIDGES",
    sub: "30-year world leader — commercial grade",
    image: "/products/fridges/731077_2e9336e23caf49a9b86ea2cb7912a68e~mv2.jpg",
    hoverImage: "/tiles/home-fridges-hover.jpg",
    to: "/12v-fridges",
  },
  {
    n: "11",
    title: "PREMIUM BULL BARS",
    sub: "ADR-tested SA-made — NZ's most premium",
    image: "/products/bullbars/731077_8cf7380786f94160a3b1a0fb6eeebfa4~mv2.jpg",
    hoverImage: "/products/bullbars/731077_0853aa6e0bf4473090edb1b8dcb638dd~mv2.webp",
    to: "/bull-bars",
  },
  {
    n: "12",
    title: "LED LIGHTING",
    sub: "Dual-colour soft-touch — best on market",
    image: "/tiles/home-led-main.jpg",
    hoverImage: "/tiles/home-led-hover.jpg",
    to: "/12v-fridges#power-lighting",
  },
  {
    n: "13",
    title: "CAMP COOKING",
    sub: "Not your average cookers",
    image: "/products/cooking/731077_3366701b62584e2e95c87b826b3b357c~mv2.png",
    hoverImage: "/tiles/home-cooking-hover.jpg",
    to: "/camp-cooking",
  },
  {
    n: "14",
    title: "ENSUITES & RELATED PRODUCTS",
    sub: "Shower tents · brackets · weather covers",
    image: "/products/ensuites/731077_a360f6691759495e94ee4e4214788831~mv2.jpg",
    hoverImage: "/products/ensuites/731077_0e57510e1667434d8db29f4a5528d2e4~mv2.jpg",
    to: "/ensuites",
  },
  {
    n: "15",
    title: "CLEARANCE & SPECIALS",
    sub: "Ex-display units · limited-time savings",
    image: "/products/clearance/731077_ab126097b9ef45969bd86ecedec251dd~mv2.jpg",
    hoverImage: "/products/rooftents/731077_9f6dcff029df4808b3ea745996181dd8~mv2.jpg",
    to: "/clearance",
  },
];

const PILLARS = [
  { icon: Globe2, kpi: "Global", label: "Expedition Proven", note: "Trusted on cross-continent 4WD expeditions" },
  { icon: ShieldCheck, kpi: "OEM", label: "Grade Construction", note: "Backed by 10-year manufacturer frame warranties" },
  { icon: Wrench, kpi: "Direct", label: "Factory Import", note: "No middlemen. Not rebranded. Not repackaged." },
  { icon: MapPin, kpi: "CHC", label: "NZ Demonstrators", note: "See the gear in person in Christchurch" },
  { icon: Compass, kpi: "1st-Hand", label: "Real Overland Experience", note: "The XpeditioNZ team lives this stuff — globally" },
  { icon: Truck, kpi: "Fleet", label: "& Dealer Approved", note: "Trusted by NZ new-vehicle dealerships" },
];

export default function HomePage() {
  const recentlyViewed = useRecentlyViewed();
  const categoryOrder = useCategoryOrder();
  const orderedCategories = sortBySlug(CATEGORIES, categoryOrder, (c) => c.to.replace(/^\//, "").split("#")[0]);

  return (
    <div data-testid="home-page">
      <SEO path="/" jsonLd={buildOrganizationJsonLd()} />
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <video
            data-testid="home-hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/products/home/home_hero_poster.jpg"
            className="md:hidden w-full h-full object-cover object-[48%_center]"
          >
            <source src="/products/home/home_hero_720.mp4" type="video/mp4" />
          </video>
          <img
            src="/products/home/home-hero-desktop-river.webp"
            alt="Xpeditionz Bush Cabin fitted Hilux fording a South Island river, snow-capped mountains in the background"
            data-testid="home-hero-image"
            className="hidden md:block w-full h-full object-cover"
            style={{ objectPosition: "58% 38%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-10% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-10 pt-20 md:pb-20 md:pt-32 w-full">
          <h1
            className="font-display text-[13vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.85] text-white hero-text-shadow fade-in-up max-w-[16ch]"
            style={{ animationDelay: "0.1s" }}
          >
            BUILT FOR REAL<br />
            <span className="text-[var(--brand-primary)]">ADVENTURE.</span><br />
            PROVEN WORLDWIDE.
          </h1>
          <div
            className="mt-6 md:mt-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8 fade-in-up"
            style={{ animationDelay: "0.3s" }}
          >
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 max-w-xl leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              Genuine, world-class overland equipment —<br className="md:hidden" /> engineered by South Africa's leading brands for the toughest conditions on Earth.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#categories" data-testid="hero-cta-explore" className="btn-primary">
                Explore The Range <ArrowUpRight size={14} />
              </a>
              <a href="#brands" data-testid="hero-cta-brands" className="btn-secondary">
                Our Brands
              </a>
            </div>
          </div>
        </div>
      </section>

      <RecentlyViewedStrip items={recentlyViewed} />

      <div className="flex flex-col">
        {/* BRAND STATEMENT (text only — always shown right after hero, before categories) */}
        <section className="border-y border-[var(--border-default)] bg-[var(--bg-secondary)]">
          <div className="container-x py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
              <div className="lg:col-span-5">
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.95] text-white">
                  NZ WAS MISSING<br />
                  <span className="text-[var(--brand-primary)]">EXPEDITION-GRADE.</span>
                </h2>
              </div>
              <div className="lg:col-span-7 lg:col-start-6 space-y-4 text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">
                <p>
                  XpeditioNZ imports genuine South African expedition brands — globally proven, professionally engineered, and built to a standard most local buyers have never experienced. While much of the market is filled with locally branded generic imports, <span className="text-white">we specialise in brands trusted by expedition travellers worldwide.</span>
                </p>
                <p>
                  This is the gear you upgrade to. Years of experience planning and running 4WD expeditions across multiple continents and some of the harshest environments on Earth is what sets us apart in New Zealand.{" "}
                  <Link to="/about" data-testid="brand-statement-about-link" className="text-[var(--brand-primary)] hover:underline inline-flex items-center gap-1 whitespace-nowrap">
                    Read our story <ArrowUpRight size={12} />
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORIES */}
        <section id="categories" className="py-10 md:py-14 scroll-mt-24">

          <div className="container-x">
            <div className="mb-6 md:mb-8">
              <p className="overline">— The Range</p>
            </div>

            <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orderedCategories.map((c, i) => {
                const isExternal = c.to.startsWith("http");
                const Tag = isExternal ? "a" : Link;
                const tagProps = isExternal ? { href: c.to, target: "_blank", rel: "noopener noreferrer" } : { to: c.to };
                return (
                  <StaggerItem key={c.to}>
                    <Tag
                      {...tagProps}
                      data-testid={`home-category-${i}`}
                      className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors"
                    >
                      <div className="aspect-[16/10] overflow-hidden relative">
                        <img
                          src={c.image}
                          alt={c.title}
                          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out scale-100 ${c.hoverImage ? "group-hover:opacity-0 group-hover:scale-110" : "group-hover:scale-105"}`}
                        />
                        {c.hoverImage && (
                          <div className="absolute inset-0 overflow-hidden" style={c.hoverImageZoom ? { transform: `scale(${c.hoverImageZoom})` } : undefined}>
                            <img
                              src={c.hoverImage}
                              alt={`${c.title} — alternate view`}
                              data-testid={`home-category-${i}-hover-image`}
                              className="absolute inset-0 w-full h-full object-cover opacity-0 scale-110 transition-all duration-700 ease-out group-hover:opacity-100 group-hover:scale-100"
                            />
                          </div>
                        )}
                        <div className={`absolute inset-0 bg-gradient-to-t ${c.gradientOverlay || "from-black/90 via-black/30 to-transparent"}`} />
                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                          <h3 className="font-display text-2xl md:text-3xl text-white leading-tight tracking-wide">{c.title}</h3>
                          <p className="text-base font-mono uppercase tracking-wider text-[var(--brand-primary)] mt-2">{c.sub}</p>
                          <span className="inline-flex items-center gap-1.5 mt-4 text-base font-mono uppercase tracking-[0.18em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
                            Explore <ArrowUpRight size={14} />
                          </span>
                        </div>
                      </div>
                    </Tag>
                  </StaggerItem>
                );
              })}

              <StaggerItem>
                <Link
                  to="/expeditions"
                  data-testid="home-expeditions-tile"
                  className="group relative block overflow-hidden border border-[var(--brand-primary)]/40 hover:border-[var(--brand-primary)] transition-colors"
                >
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img
                      src="/products/expeditions/untamed-river-crossing.webp"
                      alt="XpeditioNZ Experience"
                      className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out scale-100 group-hover:opacity-0 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 overflow-hidden">
                      <img
                        src="/products/bushcabin/731077_e0bde801920f4eb0aab3acaab7a41ee3~mv2.jpg"
                        alt="XpeditioNZ Experience — alternate view"
                        data-testid="home-expeditions-tile-hover-image"
                        className="absolute inset-0 w-full h-full object-cover opacity-0 scale-110 transition-all duration-700 ease-out group-hover:opacity-100 group-hover:scale-100"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                      <h3 className="font-display text-2xl md:text-3xl text-white leading-tight tracking-wide">XPEDITIONZ EXPERIENCE</h3>
                      <p className="text-base font-mono uppercase tracking-wider text-[var(--brand-primary)] mt-2">South Island Wilderness. Vehicles Built for it.</p>
                      <span className="inline-flex items-center gap-1.5 mt-4 text-base font-mono uppercase tracking-[0.18em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
                        Explore <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            </StaggerChildren>

            <p className="text-[var(--text-secondary)] mt-16 text-center font-mono text-base tracking-[0.2em] uppercase">
              Trade & fleet enquiries welcome · NZ-wide delivery available
            </p>
          </div>
        </section>
      </div>

      {/* OUR BRANDS (cards — shown after the product categories, on every device) */}
      <section id="brands" className="border-t border-[var(--border-default)]">
        <div className="container-x py-10 md:py-14">
          <div className="mb-6 md:mb-8">
            <p className="overline">— The Xpeditionz Difference</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            {BRANDS.map((b, i) => (
              <div key={b.name} data-testid={`brand-${b.name.toLowerCase().replace(/\s+/g, '-')}`} className="bg-[var(--bg-main)] p-6 md:p-7 hover:bg-[var(--bg-secondary)] transition-colors">
                <p className="font-mono text-base tracking-[0.3em] uppercase text-[var(--brand-primary)]">{b.origin}</p>
                <h3 className="font-display text-2xl md:text-3xl text-white mt-2 leading-none">{b.name}</h3>
                <p className="text-base font-mono uppercase tracking-wider text-[var(--text-secondary)] mt-3">{b.tagline}</p>
                <p className="text-base text-[var(--text-secondary)] mt-3 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
        <div className="container-x py-24 md:py-32">
          <div className="mb-16">
            <p className="overline mb-4">— Why Xpeditionz</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl">
              GEAR CHOSEN BY PEOPLE<br />
              WHO <span className="text-[var(--brand-primary)]">ACTUALLY USE IT.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.label} data-testid={`home-pillar-${i}`} className="min-w-0 bg-[var(--bg-main)] p-6 md:p-10 hover:bg-[var(--bg-secondary)] transition-colors">
                  <Icon size={26} className="text-[var(--brand-primary)] mb-6" strokeWidth={1.3} />
                  <p className="font-display text-4xl md:text-5xl text-white leading-none">{p.kpi}</p>
                  <p className="font-mono text-sm md:text-base uppercase tracking-[0.02em] md:tracking-[0.2em] text-[var(--brand-primary)] mt-4 break-words">{p.label}</p>
                  <p className="text-sm md:text-base text-[var(--text-tertiary)] mt-2 break-words">{p.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY TEASER */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-12">
          <div className="lg:col-span-6">
            <p className="overline mb-4">— NZ Customer Builds</p>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95]">
              REAL RIGS.<br />
              <span className="text-[var(--brand-primary)]">REAL OWNERS.</span><br />
              REAL COUNTRY.
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-[var(--text-secondary)] text-lg leading-relaxed">
              See what our customers are building. From tradies putting Bushtech canopies through their daily grind, to full overland fitouts touring the length of the South Island — the gallery below shows what a proper Xpeditionz build looks like out in the wild.
            </p>
            <a
              href="https://photos.app.goo.gl/FAy62ZWh6aVKq34D7"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-gallery-cta"
              className="btn-primary mt-8"
            >
              View Full Gallery <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[HERO_IMAGES.wellsideHilux, HERO_IMAGES.xseriesDuo, HERO_IMAGES.bushCabinSetup, HERO_IMAGES.loadDemo].map((src, i) => (
            <div key={src} className="aspect-[4/5] overflow-hidden border border-[var(--border-default)] group">
              <img
                src={src}
                alt={`Customer build ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
